import React, { useState, useEffect, useCallback } from 'react';
import { nearbyAPI } from '../../utils/api';

const BUSINESS_ICONS = {
  Restaurant: '🍽️', Cafe: '☕', Grocery: '🛒', Pharmacy: '💊',
  Hospital: '🏥', Hotel: '🏨', Bank: '🏦', School: '🏫',
  Gym: '💪', Salon: '✂️', Shop: '🛍️', Mechanic: '🔧',
  Bakery: '🥐', Clinic: '🩺', Petrol: '⛽', Default: '🏢',
};

function icon(type) {
  if (!type) return BUSINESS_ICONS.Default;
  const key = Object.keys(BUSINESS_ICONS).find(k =>
    type.toLowerCase().includes(k.toLowerCase())
  );
  return key ? BUSINESS_ICONS[key] : BUSINESS_ICONS.Default;
}

export default function NearbyBusinessTab() {
  const [businesses, setBusinesses]   = useState([]);
  const [types, setTypes]             = useState([]);
  const [filterType, setFilterType]   = useState('');
  const [radius, setRadius]           = useState(10);
  const [loading, setLoading]         = useState(false);
  const [locLoading, setLocLoading]   = useState(false);
  const [error, setError]             = useState('');
  const [coords, setCoords]           = useState(null);
  const [locationName, setLocationName] = useState('');
  const [permDenied, setPermDenied]   = useState(false);

  const fetchNearby = useCallback(async (lat, lon, type, rad) => {
    setLoading(true);
    setError('');
    try {
      const params = { lat, lon, radius: rad };
      if (type) params.type = type;
      const res = await nearbyAPI.list(params);
      if (res.data.success) {
        setBusinesses(res.data.data.businesses || []);
        setTypes(res.data.data.types || []);
      } else {
        setError(res.data.message || 'Failed to fetch nearby businesses');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  }, []);

  // No automatic fetch on mount to avoid 400 errors if user has no saved location.
  // The user must click "Use Current Location" to start.


  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setLocLoading(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lon: longitude });

        // Reverse geocode using nominatim (free, no key needed)
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const d = await r.json();
          setLocationName(d.display_name?.split(',').slice(0, 3).join(', ') || '');
        } catch { /* ignore */ }

        setLocLoading(false);
        fetchNearby(latitude, longitude, filterType, radius);
      },
      (err) => {
        setLocLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setPermDenied(true);
          setError('Location permission denied. Please allow access in your browser settings.');
        } else {
          setError('Could not get your location. Try again.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleFilter = (type) => {
    setFilterType(type);
    if (coords) {
      fetchNearby(coords.lat, coords.lon, type, radius);
    }
  };

  const handleRadius = (r) => {
    setRadius(r);
    if (coords) {
      fetchNearby(coords.lat, coords.lon, filterType, r);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-primary)' }}>

      {/* Header */}
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          📍 Nearby Businesses
        </h2>
        {locationName && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
            📌 {locationName}
          </p>
        )}
      </div>

      {/* Controls */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Use Current Location button */}
        <button
          onClick={handleGetLocation}
          disabled={locLoading || permDenied}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '10px 16px', borderRadius: 10, border: 'none', cursor: permDenied ? 'not-allowed' : 'pointer',
            background: coords ? 'var(--primary-light, #E7F3EF)' : 'var(--primary)',
            color: coords ? 'var(--primary)' : '#fff',
            fontWeight: 600, fontSize: 14, transition: 'all .2s',
          }}
        >
          {locLoading
            ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Getting location…</>
            : coords ? '✅ Location Updated — Refresh' : '📍 Use Current Location'
          }
        </button>

        {permDenied && (
          <div style={{ fontSize: 12, color: '#E53935', padding: '6px 10px', background: '#FFEBEE', borderRadius: 8 }}>
            Location access was denied. Enable it in browser settings and reload.
          </div>
        )}

        {/* Radius selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Radius:</span>
          {[5, 10, 20, 50].map(r => (
            <button key={r} onClick={() => handleRadius(r)} style={{
              padding: '4px 12px', borderRadius: 20, border: '1px solid var(--border-color)',
              background: radius === r ? 'var(--primary)' : 'var(--bg-primary)',
              color: radius === r ? '#fff' : 'var(--text-primary)',
              cursor: 'pointer', fontSize: 13, fontWeight: radius === r ? 600 : 400,
            }}>{r} km</button>
          ))}
        </div>

        {/* Type filter chips */}
        {types.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => handleFilter('')} style={{
              padding: '4px 12px', borderRadius: 20, border: '1px solid var(--border-color)',
              background: filterType === '' ? 'var(--primary)' : 'var(--bg-primary)',
              color: filterType === '' ? '#fff' : 'var(--text-primary)',
              cursor: 'pointer', fontSize: 12, fontWeight: filterType === '' ? 600 : 400,
            }}>All</button>
            {types.map(t => (
              <button key={t} onClick={() => handleFilter(t)} style={{
                padding: '4px 12px', borderRadius: 20, border: '1px solid var(--border-color)',
                background: filterType === t ? 'var(--primary)' : 'var(--bg-primary)',
                color: filterType === t ? '#fff' : 'var(--text-primary)',
                cursor: 'pointer', fontSize: 12, fontWeight: filterType === t ? 600 : 400,
              }}>{icon(t)} {t}</button>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ margin: '10px 16px 0', padding: '10px 14px', borderRadius: 10, background: '#FFEBEE', color: '#C62828', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
            <div>Searching nearby businesses…</div>
          </div>
        ) : businesses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🏙️</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No businesses found nearby</div>
            <div style={{ fontSize: 13 }}>Try a larger radius or use your current location.</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
              {businesses.length} business{businesses.length !== 1 ? 'es' : ''} found within {radius} km
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {businesses.map(b => (
                <div key={b.id} style={{
                  background: 'var(--bg-secondary)', borderRadius: 12,
                  border: '1px solid var(--border-color)', padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'box-shadow .15s',
                }}>
                  {/* Avatar / icon */}
                  <div style={{
                    width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                    background: 'var(--primary-light, #E7F3EF)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, overflow: 'hidden',
                  }}>
                    {b.avatar
                      ? <img src={b.avatar} alt={b.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
                      : icon(b.business_type)
                    }
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500, marginTop: 2 }}>
                      {icon(b.business_type)} {b.business_type}
                    </div>
                    {b.address && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📌 {b.address}
                      </div>
                    )}
                  </div>

                  {/* Distance badge */}
                  <div style={{
                    flexShrink: 0, background: 'var(--primary)', color: '#fff',
                    borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>
                    {b.distance_km < 1 ? `${Math.round(b.distance_km * 1000)} m` : `${b.distance_km} km`}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
